<script lang="ts" setup>
import { useElementSize } from '@vueuse/core';
import Popover from 'primevue/popover';
import { ref, computed, watch } from 'vue';
import { useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import AppLogo from '@/app/assets/AppLogo.vue';
import AppLogoIcon from '@/app/assets/AppLogoIcon.vue';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useProfileStore } from '@/store/profileStore';
import { useProjectStore } from '@/store/projectStore';
import { useTemplateStore } from '@/store/templateStore';
import { useUiStore } from '@/store/uiStore';
import { useUserRulesStore } from '@/store/userRulesStore';
import { useViewportBelow } from '@/theme/breakpoints';
import AppSideNavigationItem from './AppSideNavigationItem.vue';
import AppSideNavigationUser from './AppSideNavigationUser.vue';
import type { SideNavigationItemProps } from './AppSideNavigationItem.vue';

type SideNavigationButton = {
  id: string;
  shouldRender: boolean;
  overflowOrder?: number;
} & SideNavigationItemProps;

type Mode = 'minimal' | 'full';

const route = useRoute();
const root = ref<HTMLElement | null>(null);
const mini = useViewportBelow('nav-full');
const templateStore = useTemplateStore();
const projectStore = useProjectStore();
const userRulesStore = useUserRulesStore();
const uiStore = useUiStore();
uiStore.setCollapseNavbar();
uiStore.setAIAssistantPreferences();

const showTemplateItems = computed(() => {
  const hasLoadedTemplate =
    !!route.params.templateId &&
    templateStore.hasTemplate(route.params.templateId as string);

  return hasLoadedTemplate;
});

const showProjectItems = computed(
  () =>
    !!route.params.projectId &&
    projectStore.hasProject(route.params.projectId as string),
);

const templateId = computed(() => route.params.templateId as string);
const projectId = computed(() => route.params.projectId as string);

const canUserViewSettings = computed(() => {
  const userRoles = useProfileStore().profile.roles || [];
  const allowedRoles = [
    'MANAGE_USERS',
    'MANAGE_SCOPES',
    'MANAGE_PROJECT_RESPONSIBLE',
    'MANAGE_TEMPLATE_OWNER',
    'MANAGE_IN_APP_CONTENT',
    'GLOBAL_CONTENT_ADMIN',
  ];

  return userRoles.some((r) => allowedRoles.includes(r));
});

const settingsFirstTab = computed(() => {
  const userRoles = useProfileStore().profile.roles;
  if (userRoles?.includes('MANAGE_USERS')) {
    return 'CompanyUsers';
  }
  if (userRoles?.includes('MANAGE_SCOPES')) {
    return 'CompanyScopes';
  }
  if (userRoles?.includes('MANAGE_PROJECT_RESPONSIBLE')) {
    return 'CompanyProjects';
  }
  if (userRoles?.includes('MANAGE_TEMPLATE_OWNER')) {
    return 'CompanyTemplates';
  }
  if (
    userRoles?.includes('MANAGE_IN_APP_CONTENT') ||
    userRoles?.includes('GLOBAL_CONTENT_ADMIN')
  ) {
    return 'CompanyInAppContent';
  }

  return '';
});

const canUserViewLibrary = computed(() => {
  const userRoles = useProfileStore().profile.roles || [];

  return (
    userRoles.includes('MANAGE_FUNCTIONS') ||
    userRoles.includes('LIBRARY_MANAGER')
  );
});

// Feature flag + the optional per-user allowlist, both owned by the store so
// that every surface exposing meeting content gates on the same rule.
const canUserViewMeetingTranscripts = computed(
  () => useFeatureFlagStore().canAccessMeetingTranscripts,
);

const isFull = computed(() => {
  return uiStore.navbarMode === 'uncollapsed' && !mini.value;
});
const mode = computed((): Mode => {
  return isFull.value ? 'full' : 'minimal';
});

const isMobile = useViewportBelow('sm');

const isMobileOpen = ref(false);

const topLevelNavigationItems = computed((): SideNavigationButton[] => {
  return [
    {
      id: 'side-bar-dashboard',
      icon: 'Home',
      label: $t('sideNavigation.myBeeboard'),
      to: { name: 'Dashboard' },
      mode: mode.value,
      activeRoutes: ['DashboardRoot'],
      shouldRender: true,
    },
    {
      id: 'side-bar-project-reporting',
      icon: 'Gauge',
      label: $t('sideNavigation.projectReports'),
      to: { name: 'CompanyProjectReports' },
      mode: mode.value,
      shouldRender: userRulesStore.canViewDashboards(),
      overflowOrder: 3,
    },
    {
      id: 'side-bar-projects',
      icon: 'BeeProjects',
      label: $t('sideNavigation.projects'),
      to: { name: 'Projects' },
      mode: mode.value,
      shouldRender: true,
    },
    {
      id: 'side-bar-templates',
      icon: 'FolderRoot',
      label: $t('sideNavigation.templates'),
      to: '/templates',
      mode: mode.value,
      shouldRender: true,
    },
    {
      id: 'side-bar-views',
      icon: 'ChartNoAxesGantt',
      label: $t('views.title'),
      to: { name: 'ViewsList' },
      mode: mode.value,
      activeRoutes: ['ViewResults', 'ManagedViewResults'],
      shouldRender: useFeatureFlagStore().hasViews,
    },
    {
      id: 'side-bar-meeting-transcripts',
      icon: 'FileText',
      label: $t('project.transcript.meetingSummaries'),
      to: { name: 'MeetingTranscripts' },
      mode: mode.value,
      activeRoutes: ['MeetingSummaries', 'MeetingSummaryDetail'],
      shouldRender: canUserViewMeetingTranscripts.value,
      overflowOrder: 4,
    },
    {
      id: 'side-bar-settings',
      icon: 'Settings',
      label: $t('sideNavigation.companySettings'),
      to: { name: settingsFirstTab.value },
      mode: mode.value,
      activeRoutes: ['CompanySettings'],
      shouldRender: canUserViewSettings.value && !isMobile.value,
      overflowOrder: 1,
    },
    {
      id: 'side-bar-library',
      icon: 'Library',
      label: $t('sideNavigation.library'),
      to: { name: 'CompanyFunctions' },
      mode: mode.value,
      activeRoutes: ['CompanyLibrary'],
      shouldRender: canUserViewLibrary.value && !isMobile.value,
      overflowOrder: 2,
    },
  ];
});

// Overflow: dynamically hide low-priority top-level items into a "More" popover
const ITEM_HEIGHT = 42; // approximate px per nav item (38px content + 4px margin)
const SEPARATOR_HEIGHT = 30; // px for a .separator element

const mainItemsRef = ref<HTMLElement | null>(null);
const { height: mainItemsHeight } = useElementSize(mainItemsRef);

// These counts MUST match the number of items `shouldRender`s as true in
// SideNavigationProjects / SideNavigationTemplates for the given viewport.
// They feed the top-level overflow calculation; if they drift, the reserved
// space is wrong and a vertical scrollbar reappears. Update them whenever a
// context nav item is added or removed.
// Projects: desktop renders 9 items; mobile renders 3 (overview, list,
// meetings). The Kanban board is not a nav item — it is reached via the
// List/Board switcher in the list/board page header.
const contextItemCount = computed(() => {
  if (showProjectItems.value) {
    return isMobile.value ? 3 : 9;
  }
  if (showTemplateItems.value) {
    return isMobile.value ? 1 : 6;
  }

  return 0;
});

const contextSectionHeight = computed(() => {
  if (contextItemCount.value === 0) {
    return 0;
  }

  return contextItemCount.value * ITEM_HEIGHT + SEPARATOR_HEIGHT;
});

const renderableItems = computed(() =>
  topLevelNavigationItems.value.filter((i) => i.shouldRender),
);

const overflowState = computed(() => {
  const available = mainItemsHeight.value - contextSectionHeight.value;
  const maxSlots = Math.floor(available / ITEM_HEIGHT);
  const items = renderableItems.value;

  if (items.length <= maxSlots) {
    return { visible: items, overflow: [] as SideNavigationButton[] };
  }

  // Reserve one slot for the "More" button
  const slotsForItems = Math.max(0, maxSlots - 1);
  const alwaysVisible = items.filter((i) => !i.overflowOrder);
  const canOverflow = items
    .filter((i) => i.overflowOrder !== undefined)
    .sort((a, b) => a.overflowOrder! - b.overflowOrder!);

  const slotsForOverflowable = Math.max(
    0,
    slotsForItems - alwaysVisible.length,
  );
  const toHide = canOverflow.slice(
    0,
    canOverflow.length - slotsForOverflowable,
  );
  const toHideIds = new Set(toHide.map((i) => i.id));

  return {
    visible: items.filter((i) => !toHideIds.has(i.id)),
    overflow: toHide.reverse(),
  };
});

const visibleTopLevelItems = computed(() => overflowState.value.visible);
const overflowItems = computed(() => overflowState.value.overflow);
const hasOverflow = computed(() => overflowItems.value.length > 0);

const morePopover = ref();
function toggleMoreMenu(event: Event) {
  morePopover.value?.toggle(event);
}

function handleMobileToggle() {
  isMobileOpen.value = !isMobileOpen.value;
}

watch(
  () => route.path,
  () => morePopover.value?.hide(),
);

const toggleButtonRef = useTemplateRef<HTMLButtonElement>('toggleButton');
function handleClick() {
  uiStore.toggleCollapseNavbar();
  toggleButtonRef.value?.blur();
}
</script>

<template>
  <div class="side-navigation-wrapper">
    <button
      v-if="isMobile"
      class="mobile-toggle-button"
      @click="handleMobileToggle"
    >
      <UiIcon :name="isMobileOpen ? 'ChevronsLeft' : 'ChevronsRight'" />
    </button>
    <div
      ref="root"
      class="app-side-navigation"
      :data-mode="mode"
      :class="{ mobile: isMobile, open: isMobileOpen }"
    >
      <div class="navigation">
        <div class="header" :class="{ collapsed: !isFull }">
          <a v-if="!isMobile" href="/">
            <AppLogo v-if="isFull" class="logo" />
            <AppLogoIcon v-else class="logo-icon" />
          </a>
          <Transition name="fade" mode="out-in">
            <!-- Desktop toggle button: hide on mobile -->
            <button
              v-if="!isMobile && !mini"
              ref="toggleButton"
              class="scroll-clicking"
              @click="handleClick"
            >
              <UiIcon :name="isFull ? 'ChevronsLeft' : 'ChevronsRight'" />
            </button>
          </Transition>
        </div>
        <div class="separator" />

        <div ref="mainItemsRef" class="main-items">
          <div class="scroll-section">
            <div class="section">
              <AppSideNavigationItem
                v-for="item in visibleTopLevelItems"
                :id="item.id"
                :key="item.id"
                :to="item.to"
                :icon="item.icon"
                :mode="item.mode"
                :label="item.label"
                :active-routes="item.activeRoutes"
              />
              <button
                v-if="hasOverflow"
                class="app-side-navigation-item more-button"
                :data-mode="mode"
                @click="toggleMoreMenu"
              >
                <UiIcon name="Ellipsis" class="icon" />
                <Transition name="fade">
                  <div v-if="isFull" class="info">
                    <span class="label">{{ $t('sideNavigation.more') }}</span>
                  </div>
                </Transition>
              </button>
            </div>

            <transition name="fade">
              <div v-if="showTemplateItems" class="section template">
                <div class="separator" />
                <SideNavigationTemplates
                  :full-mode="isFull"
                  :template-id="templateId"
                  :is-mobile="isMobile"
                />
              </div>
              <div v-else-if="showProjectItems" class="section project">
                <div class="separator" />
                <SideNavigationProjects
                  :full-mode="isFull"
                  :project-id="projectId"
                  :is-mobile="isMobile"
                />
              </div>
            </transition>
          </div>
        </div>
      </div>
      <div class="bottom">
        <div class="separator" />
        <AppSideNavigationUser id="bottom-user" :mode="mode" />
      </div>
    </div>
    <Popover ref="morePopover" :pt="{ content: { style: { padding: '0' } } }">
      <div class="more-menu">
        <AppSideNavigationItem
          v-for="item in overflowItems"
          :id="item.id"
          :key="item.id"
          :to="item.to"
          :icon="item.icon"
          mode="full"
          :label="item.label"
          :active-routes="item.activeRoutes"
        />
      </div>
    </Popover>
  </div>
</template>

<style lang="scss" scoped>
.side-navigation-wrapper {
  height: 100vh;
}
.app-side-navigation {
  background-color: var(--color-gray-0);
  height: 100%;
  padding: var(--space-16) var(--space-10);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  transition-timing-function: ease-out;
  transition-property: width;
  transition-duration: var(--duration-slow);
  box-shadow: 0 1px 4px 0 rgb(0 0 0 / 7%);

  &[data-mode='minimal'] {
    .scroll-clicking {
      left: calc(120%);
    }
    width: px(72);
  }

  &[data-mode='full'] {
    width: px(296);
  }
}

.navigation {
  width: 100%;
}

.main-items {
  padding: px(0) var(--space-6);
  height: calc(100vh - px(189));
  overflow: auto;
}

.scroll-clicking {
  display: flex;
  align-items: center;
  position: absolute;
  left: calc(104%);
  border-radius: px(0) var(--border-radius-14) var(--border-radius-14) px(0);
  background: var(--color-primary-50);
  width: px(24);
  height: px(68);
  z-index: 2;
  padding: var(--space-2);

  svg {
    stroke: var(--color-primary-900);
    margin: 0;
  }
  &:hover {
    background: var(--color-primary-400);
    svg {
      stroke: white;
    }
  }
}

.logo {
  width: px(160);
  margin: var(--space-8);
  left: px(2);
  position: relative;
}

.logo-icon {
  width: px(40);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: px(40);
  position: relative;
}
.header.collapsed {
  justify-content: center;
}

.separator {
  border-top: 1px solid var(--color-gray-200);
  margin: px(15) 0 calc(#{px(15)} - 1px);
}

.bottom {
  width: 100%;
  margin-bottom: var(--space-8);
}

.button-class {
  border: 0;
  background-color: white;
}

.more-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--space-8);
  border: none;
  background: none;
  border-radius: var(--border-radius-6);
  cursor: pointer;
  color: var(--color-gray-600);
  transition-property: color, background-color;
  transition-duration: var(--duration-base);
  transition-timing-function: ease-out;

  &:hover {
    background-color: var(--color-primary-100);
    color: var(--color-primary-800);
    transition-duration: 0s;
  }

  .icon {
    width: px(22);
    height: px(22);
    flex: 0 0 auto;
  }

  .info {
    margin-left: var(--space-8);
    overflow: hidden;
  }

  .label {
    white-space: nowrap;
  }
}

.more-menu {
  min-width: px(200);
  padding: var(--space-4);
}

@include below(sm) {
  .app-side-navigation.mobile {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: px(72);
    transform: translateX(-100%);
    z-index: 20;
  }
  .app-side-navigation.mobile.open {
    transform: translateX(0);
  }

  .mobile-toggle-button {
    position: fixed;
    top: 25px;
    left: 23px;
    z-index: 21;
    width: px(30);
    height: px(30);
    background: var(--color-primary-50);
    border: none;
    padding-left: 3px;
    border-radius: var(--border-radius-4);
  }
}
</style>
